/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import {batch, computed, signal} from '@preact/signals-react';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  checkScriptNameTaken,
  checkScriptVersionTaken,
  getPlatformDeviceTypes
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account as AccountType,
  Peg2Module,
  Peg2ModuleProfile,
  PegWizardScript,
  PlatformDeviceTypes,
  ValidationMetaData
} from '@ion/core/src/types/entities';
import {
  getScriptNameWithExtension,
  isValidHostname,
  isValidIPAddress,
  isValidPort,
  isValidScriptVersion
} from '@ion/core/src/utils/DeviceUtils';
import {isValidDomain} from '@ion/core/src/utils/Validators';
import changePage, {Route} from '@/routes/RouteDefinitions';
import FeatureFlags, {FeatureFlag} from '../../../security/FeatureFlags';
import Features from '../../../security/Features';
import {
  buildWakeSleepDefaults,
  composeModule,
  flattenGroupDefaults,
  getListGroupDefaults
} from './ModuleComposer';
import {
  CompletedSteps,
  ComposedParameterGroup,
  StepVisited,
  WAKE_SLEEP_MODULE_TYPE,
  WakeSleepFlatValues,
  WizardStep
} from './types';

export type ModuleValues = Record<string, Record<string, number | boolean | string>>;
export type AccountOptions = {globalId: number; accounts: AccountType[]};
export const showPegWizard    = signal(false);
export const setShowPegWizard = (value: boolean) => {
  showPegWizard.value = value;
};

/**
 * The route to navigate back to when the user confirms Cancel, set by an
 * external entry point (e.g. the provisioning/devicefiles page) just before
 * it opens the wizard.
 */
export const wizardReturnRoute    = signal<Route | undefined>(undefined);
export const setWizardReturnRoute = createMutator<Route | undefined>(wizardReturnRoute);

/**
 * The route of an in-app or browser back / forward navigation which the exit
 * guard blocked while the form had unsaved changes. Exit Without Saving
 * continues to it, Continue Editing drops it.
 */
export const pendingRoute    = signal<string | undefined>(undefined);
export const setPendingRoute = createMutator<string | undefined>(pendingRoute);

// ... existing isEnabled export, states, etc...
export const isEnabled = () => (
  Features.hasPEGWizard() && FeatureFlags.isEnabled(FeatureFlag.PEG_WIZARD)
);

// access is determined via the Features API exclusively (ADM-4425) ...
export const hasPegWizardCreate = () => Features.hasPEGWizard();

export const hasPegWizardUpdate = () => Features.hasPEGWizard();

export const activeStep    = signal<WizardStep>(WizardStep.SCRIPT_DETAILS);
export const setActiveStep = createMutator<WizardStep>(activeStep);

export const isFirstStep = computed(() => (
  activeStep.value === WizardStep.SCRIPT_DETAILS)
);
export const isLastStep  = computed(() => (
  activeStep.value === WizardStep.GENERATE
));

// Wizard steps that display the persistent Script Details context bar (the
// Modules step covers both Module Selection and Module Configuration)...
const CONTEXT_BAR_STEPS: WizardStep[] = [WizardStep.WAKE_SLEEP, WizardStep.MODULES];

export const showScriptContextBar = computed(() => (
  CONTEXT_BAR_STEPS.includes(activeStep.value)
));

// Define initial enable-disable state for Steps
const initialStepVisited: StepVisited = {
  [WizardStep.SCRIPT_DETAILS] : true,
  [WizardStep.WAKE_SLEEP]     : false,
  [WizardStep.MODULES]        : false,
  [WizardStep.REVIEW]         : false,
  [WizardStep.GENERATE]       : false
};

export const stepVisited    = signal<StepVisited>(initialStepVisited);
export const setStepVisited = createMutator<StepVisited>(stepVisited);

export const visitStep = (step: WizardStep): void => {
  if (!stepVisited.value[step]) {
    setStepVisited?.({...stepVisited.value, [step]: true});
  }
};

/**
 * Leaving the Generate step discards its post-generate notifications and
 * locks Generate again, so it is only re-entered through Next - which
 * re-assembles the script - and a stale success message never shows up
 * ahead of the new result (ADM-6053).
 */
const leaveGenerateStep = (): void => {
  setShowScriptGeneratedSnackbar?.(false);
  setShowGenerateSnackbar?.(false);
  setStepVisited?.({...stepVisited.value, [WizardStep.GENERATE]: false});
};

export const navigateToStep = (step: WizardStep): void => {
  // an in-flight generation would otherwise land on a step the user has left...
  if (!isGenerating.value) {
    batch(() => {
      if (activeStep.value === WizardStep.GENERATE && step !== WizardStep.GENERATE) {
        leaveGenerateStep();
      }
      setActiveStep(step);
      visitStep(step);
    });
  }
};

/**
 * Tracks which steps the user has successfully advanced past with Next, kept
 * separate from `activeStep` so a step's tick survives backward navigation.
 */
const initialCompletedSteps: CompletedSteps = {
  [WizardStep.SCRIPT_DETAILS] : false,
  [WizardStep.WAKE_SLEEP]     : false,
  [WizardStep.MODULES]        : false,
  [WizardStep.REVIEW]         : false,
  [WizardStep.GENERATE]       : false
};

export const completedSteps    = signal<CompletedSteps>(initialCompletedSteps);
export const setCompletedSteps = createMutator<CompletedSteps>(completedSteps);

export const markStepCompleted = (step: WizardStep): void => {
  if (!completedSteps.value[step]) {
    setCompletedSteps?.({...completedSteps.value, [step]: true});
  }
};

/**
 * A saved script has already been through every step up to Review, so Edit
 * opens with those steps completed and visited. Generate stays locked so it
 * is only reached through Next, which re-assembles the script.
 */
const EDIT_PRE_COMPLETED_STEPS: WizardStep[] = [
  WizardStep.SCRIPT_DETAILS,
  WizardStep.WAKE_SLEEP,
  WizardStep.MODULES,
  WizardStep.REVIEW
];

export const markEditStepsCompleted = (): void => {
  batch(() => {
    EDIT_PRE_COMPLETED_STEPS.forEach(function(step) {
      markStepCompleted(step);
      visitStep(step);
    });
  });
};

export const goToNextStep = (): void => {
  if (!isLastStep.value) {
    const completingStep = activeStep.value;
    const nextStep       = activeStep.value + 1 as WizardStep;

    if (nextStep === WizardStep.GENERATE) {
      setAssembledScript?.(null);
      setGenerateError?.(null);
      setCanRetryGenerate?.(false);
      setShowScriptGeneratedSnackbar?.(false);
      setShowGenerateSnackbar?.(false);
      setScriptSaved?.(false);
    }
    markStepCompleted(completingStep);
    navigateToStep(nextStep);
  }
};

export const goToPreviousStep = (): void => {
  if (!isFirstStep.value) {
    navigateToStep(activeStep.value - 1 as WizardStep);
  }
};

export const resetWizard = (): void => {
  setActiveStep(WizardStep.SCRIPT_DETAILS);
  setStepVisited?.(initialStepVisited);
  setCompletedSteps?.(initialCompletedSteps);
  setSelectedModules?.([]);
  setActiveModuleIndex?.(undefined);
  setModuleValues?.({});
  setScriptName?.('');
  setScriptDescription?.('');
  setScriptVersion?.('');
  setScriptVersionError?.('');
  setScriptNameError?.('');
  setVehicleType?.('');
  setSelectedAccount?.(undefined);
  setSavedAccount?.(undefined);
  setAccountOptions?.(undefined);
  setIsLoadingAccount?.(false);
  setDeviceModel?.('');
  setDeviceModelOptions?.([]);
  setLoadingDeviceModel?.(false);
  setSimApn?.('');
  setInboundServerAddress?.('');
  setInboundServerAddressError?.('');
  setPort?.('');
  setPortError?.('');
  setModuleProfile?.(undefined);
  setLoadingScriptVersion?.(false);
  setWakeSleepValues?.({});
  setAssembledScript?.(null);
  setGenerateError?.(null);
  setCanRetryGenerate?.(false);
  setIsGenerating?.(false);
  setShowGenerateSnackbar?.(false);
  setShowScriptGeneratedSnackbar?.(false);
  setScriptSaved?.(false);
  setPlatformId?.('');
  setPlatformName?.('');
  setDeviceTypeId?.('');
  setDeviceTypeName?.('');
  setIsEditMode?.(false);
  setIsLoadingWizardData?.(false);
  clearDefaultSnapshots();
  // additional field resets appended per step tickets ...
  markClean();
};

export const showCancelConfirmation = signal<boolean>(false);
export const setCancelConfirmation  = createMutator<boolean>(showCancelConfirmation);
export const showResetConfirmation  = signal<boolean>(false);
export const setResetConfirmation   = createMutator<boolean>(showResetConfirmation);

/**
 * Cancel button handler. Opens the exit confirmation dialog when the form has
 * unsaved changes, otherwise exits directly.
 */
export const onCancelWizard = (): void => {
  if (isDirty.value) {
    setCancelConfirmation(true);
  } else {
    onConfirmCancel();
  }
};

/**
 * Continue Editing handler for the exit confirmation dialog. Closes the dialog
 * and drops any blocked navigation, form state is untouched.
 */
export const onContinueEditing = (): void => {
  batch(() => {
    setCancelConfirmation?.(false);
    setPendingRoute?.(undefined);
  });
};

/**
 * Exit Without Saving handler for the exit confirmation dialog, also used by
 * a clean Cancel. Resets the wizard, then continues to the blocked navigation
 * when one is pending, otherwise returns to the launching page: the
 * `wizardReturnRoute` when an entry point set it, else the start card.
 */
export const onConfirmCancel = (): void => {
  setCancelConfirmation(false);
  resetWizard();
  if (pendingRoute.value) {
    setWizardReturnRoute?.(undefined);
    changePage(pendingRoute.value as Route);
    setPendingRoute?.(undefined);
  } else {
    if (wizardReturnRoute.value) {
      changePage(wizardReturnRoute.value);
    } else {
      setShowPegWizard?.(false);
    }
    setWizardReturnRoute?.(undefined);
  }
};

export const onResetSettings = (): void => {
  setResetConfirmation(true);
};

export const onConfirmResetSettings = (): void => {
  batch(() => {
    setResetConfirmation?.(false);
    resetCurrentPageToDefaults();
  });
};

export const onDismissResetSettings = (): void => {
  setResetConfirmation(false);
};

// Form state
export const scriptName                = signal<string>('');
export const scriptDescription         = signal<string>('');
export const scriptVersion             = signal<string>('');
export const scriptVersionError        = signal<string>('');
export const loadingScriptVersion      = signal<boolean>(false);
export const vehicleType               = signal<string>('');
export const deviceModel               = signal<string>('');
export const deviceModelOptions        = signal<string[]>([]);
export const loadingDeviceModel        = signal<boolean>(false);
export const selectedAccountId         = signal<number | undefined>(undefined);
export const selectedAccount           = signal<AccountType | undefined>(undefined);
// the account saved with the script being edited, only set when that script loads...
export const savedAccount              = signal<AccountType | undefined>(undefined);
// the account chooser's loaded tree and the global account it was loaded for, so a remount skips the fetch...
export const accountOptions            = signal<AccountOptions | undefined>(undefined);
// set while the account chooser loads its list, so the wizard cannot leave step 1 mid-load...
export const isLoadingAccount          = signal<boolean>(false);
export const simApn                   = signal<string>('');
export const inboundServerAddress      = signal<string>('');
export const scriptNameError           = signal<string>('');
export const inboundServerAddressError = signal<string>('');
export const port                      = signal<string>('');
export const portError                 = signal<string>('');
export const moduleProfile             = signal<Peg2ModuleProfile | undefined>(undefined);
export const allModuleProfiles         = signal<Peg2ModuleProfile[] | []>([]);

/**
 * The module profile's `includedModules`, excluding the `core`-type wake/sleep
 * module: that module is configured separately on step 2 (`WakeSleepStep`), so
 * step 3 (Modules) must not offer it again as a selectable/configurable module.
 */
export const selectableModules = computed<Peg2Module[]>(() => (
  (moduleProfile.value?.includedModules ?? []).filter(
    (module) => module.pegWizardModule.moduleType !== WAKE_SLEEP_MODULE_TYPE
  )
));
export const selectedModules      = signal<string[]>([]);

/**
 * The subset of `selectableModules` the user has checked in step 3 (Modules),
 * in `selectableModules` order. `ModuleConfiguration`/`ModuleNavigation` must
 * page through this list - not the full `selectableModules` list - so Edit
 * only navigates between the modules the user actually selected.
 */
export const selectedModuleList = computed<Peg2Module[]>(() => (
  selectableModules.value.filter(
    (module) => selectedModules.value.includes(module.pegWizardModule.name)
  )
));
export const activeModuleIndex    = signal<number | undefined>(undefined);
export const moduleValues         = signal<ModuleValues>({});
export const selectedModuleNames  = signal<string[]>(['driverBehavior']);
export const wakeSleepValues      = signal<Record<string, number | boolean | string>>({});

export const setScriptName                = createMutator<string>(scriptName);
export const setScriptDescription         = createMutator<string>(scriptDescription);
export const setScriptVersion             = createMutator<string>(scriptVersion);
export const setScriptVersionError        = createMutator<string>(scriptVersionError);
export const setLoadingScriptVersion      = createMutator<boolean>(loadingScriptVersion);
export const setVehicleType               = createMutator<string>(vehicleType);
export const setDeviceModel               = createMutator<string>(deviceModel);
export const setDeviceModelOptions        = createMutator<string[]>(deviceModelOptions);
export const setLoadingDeviceModel        = createMutator<boolean>(loadingDeviceModel);
export const setSelectedAccountId         = createMutator<number | undefined>(selectedAccountId);
export const setSelectedAccount           = createMutator<AccountType | undefined>(selectedAccount);
export const setSavedAccount              = createMutator<AccountType | undefined>(savedAccount);
export const setAccountOptions            = createMutator<AccountOptions | undefined>(accountOptions);
export const setIsLoadingAccount          = createMutator<boolean>(isLoadingAccount);
export const setSimApn                    = createMutator<string>(simApn);
export const setInboundServerAddress      = createMutator<string>(inboundServerAddress);
export const setScriptNameError           = createMutator<string>(scriptNameError);
export const setInboundServerAddressError = createMutator<string>(inboundServerAddressError);
export const setPort                      = createMutator<string>(port);
export const setPortError                 = createMutator<string>(portError);
export const setModuleProfile             = createMutator<Peg2ModuleProfile | undefined>(moduleProfile);
export const setAllModuleProfiles         = createMutator<Peg2ModuleProfile[] | []>(allModuleProfiles);
export const setSelectedModules           = createMutator<string[]>(selectedModules);
export const setActiveModuleIndex         = createMutator<number | undefined>(activeModuleIndex);
export const setModuleValues              = createMutator<ModuleValues>(moduleValues);
export const setSelectedModuleNames       = createMutator<string[]>(selectedModuleNames);
export const setWakeSleepValues           = createMutator<Record<string, number | boolean | string>>(wakeSleepValues);

/**
 * `true` while the selected account is still the one saved with the script
 * being edited, meaning the user has not picked a different account. Always
 * `false` in create mode, where no account is saved.
 */
export const isSavedAccountSelected = (): boolean => (
  !!savedAccount.peek() && entityId(selectedAccountId.peek()) === entityId(savedAccount.peek())
);

/**
 * Immutable snapshots of the values that "Reset to Defaults" restores. Create
 * Mode captures the module profile's defaults whenever the device model
 * changes; Edit Mode captures the saved script's values once they are loaded.
 */
export const defaultModuleValues       = signal<ModuleValues>({});
export const defaultWakeSleepValues    = signal<WakeSleepFlatValues>({});
export const setDefaultModuleValues    = createMutator<ModuleValues>(defaultModuleValues);
export const setDefaultWakeSleepValues = createMutator<WakeSleepFlatValues>(defaultWakeSleepValues);

const isWakeSleepModule = (module: Peg2Module): boolean => (
  module.pegWizardModule.moduleType === WAKE_SLEEP_MODULE_TYPE
);

/**
 * Replaces both snapshots with deep clones of the given values, so later edits
 * to the live page values can never reach the snapshots.
 */
export const setDefaultSnapshots = (moduleDefaults: ModuleValues, wakeSleepDefaults: WakeSleepFlatValues): void => {
  batch(() => {
    setDefaultModuleValues?.(cloneDeep(moduleDefaults));
    setDefaultWakeSleepValues?.(cloneDeep(wakeSleepDefaults));
  });
};

/**
 * Replaces the default snapshots with the defaults composed from the given
 * module profile - no request is made, the profile is the one already loaded
 * for the selected device model. Passing no profile clears both snapshots.
 */
export const captureDefaultSnapshots = (profile?: Peg2ModuleProfile): void => {
  const includedModules = profile?.includedModules ?? [];
  const wakeSleepModule = includedModules.find(isWakeSleepModule);
  const otherModules    = includedModules.filter((module) => !isWakeSleepModule(module));

  setDefaultSnapshots(
    buildDefaults(otherModules),
    wakeSleepModule ? buildWakeSleepDefaults(composeModule(wakeSleepModule)) : {}
  );
};

export const clearDefaultSnapshots = (): void => {
  captureDefaultSnapshots(undefined);
};

/**
 * The name of the module currently displayed on its configuration page, or an
 * empty string while no module configuration page is open.
 */
export const activeModuleName = computed<string>(() => (
  activeModuleIndex.value === undefined
    ? ''
    : selectedModuleList.value[activeModuleIndex.value]?.pegWizardModule.name ?? ''
));

const hasWakeSleepDefaults = (): boolean => Object.keys(defaultWakeSleepValues.value).length > 0;

/**
 * Restores only `wakeSleepValues` from its snapshot. A no-op while no snapshot
 * exists, so values are never wiped when defaults were not captured.
 */
export const resetWakeSleepToDefaults = (): void => {
  if (hasWakeSleepDefaults()) {
    setWakeSleepValues?.(cloneDeep(defaultWakeSleepValues.value));
  }
};

/**
 * Restores only the named module's values from its snapshot, leaving every
 * other module untouched. A no-op while the module has no snapshot.
 */
export const resetModuleToDefaults = (moduleName: string): void => {
  const defaults = defaultModuleValues.value[moduleName];

  if (defaults) {
    setModuleValues?.({...moduleValues.value, [moduleName]: cloneDeep(defaults)});
  }
};

/**
 * Resets the currently displayed configuration page: the Wake/Sleep step, or
 * the open module configuration page on the Modules step.
 */
export const resetCurrentPageToDefaults = (): void => {
  if (activeStep.value === WizardStep.WAKE_SLEEP) {
    resetWakeSleepToDefaults();
  } else if (activeModuleIndex.value !== undefined) {
    resetModuleToDefaults(activeModuleName.value);
  }
};

const isSameValue = (current: unknown, defaultValue: unknown): boolean => (
  typeof current === 'object' || typeof defaultValue === 'object'
    ? isEqual(current, defaultValue)
    : String(current) === String(defaultValue)
);

/**
 * `true` when any value present on the page differs from its snapshot. Values
 * are compared as strings since a numeric default can arrive as a numeric
 * string from the API, and a key not yet on the page still shows its default.
 */
const hasChangesFromDefaults = (current: WakeSleepFlatValues, defaults: WakeSleepFlatValues): boolean => (
  !!current && !!defaults && Object.keys(defaults).some(
    (key) => key in current && !isSameValue(current[key], defaults[key])
  )
);

/**
 * `true` once the Wake/Sleep values differ from their snapshot - enables the
 * Reset to Defaults button on the Wake/Sleep page (disabled otherwise).
 */
export const isWakeSleepModified = computed<boolean>(() => (
  hasChangesFromDefaults(wakeSleepValues.value, defaultWakeSleepValues.value)
));

/**
 * `true` once the open module's values differ from its snapshot - enables the
 * Reset to Defaults button on a module configuration page (disabled otherwise).
 */
export const isActiveModuleModified = computed<boolean>(() => (
  hasChangesFromDefaults(
    moduleValues.value[activeModuleName.value],
    defaultModuleValues.value[activeModuleName.value]
  )
));

export const isEditMode             = signal<boolean>(false);
export const setIsEditMode          = createMutator<boolean>(isEditMode);
export const isCreateMode           = computed(() => !isEditMode.value);
export const isLoadingWizardData    = signal<boolean>(false);
export const setIsLoadingWizardData = createMutator<boolean>(isLoadingWizardData);
export const validationMetaData     = signal<ValidationMetaData>({});
export const setvalidationMetaData  = createMutator<ValidationMetaData>(validationMetaData);

export const peg2AssemblyId     = signal<string>('');
export const assembledScript    = signal<PegWizardScript | null>(null);
export const setPeg2AssemblyId  = createMutator<string>(peg2AssemblyId);
export const setAssembledScript = createMutator<PegWizardScript | null>(assembledScript);
export const generateError      = signal<string | null>(null);
export const setGenerateError   = createMutator<string | null>(generateError);

/**
 * Whether the last `generateError` came from a temporary assemble/update
 * failure, so the Generate step offers Retry with the unchanged configuration.
 */
export const canRetryGenerate    = signal<boolean>(false);
export const setCanRetryGenerate = createMutator<boolean>(canRetryGenerate);

export const isGenerating    = signal<boolean>(false);
export const setIsGenerating = createMutator<boolean>(isGenerating);

export const showGenerateSnackbar    = signal<boolean>(false);
export const setShowGenerateSnackbar = createMutator<boolean>(showGenerateSnackbar);

export const showScriptGeneratedSnackbar    = signal<boolean>(false);
export const setShowScriptGeneratedSnackbar = createMutator<boolean>(showScriptGeneratedSnackbar);

export const scriptSaved    = signal<boolean>(false);
export const setScriptSaved = createMutator<boolean>(scriptSaved);

export const fileId            = signal<number | undefined>(undefined);
export const fileVersion       = signal<number | undefined>(undefined);
export const platformId        = signal<string>('');
export const platformName      = signal<string>('');
export const deviceTypeId      = signal<string>('');
export const deviceTypeName    = signal<string>('');
export const setFileId         = createMutator<number | undefined>(fileId);
export const setFileVersion    = createMutator<number | undefined>(fileVersion);
export const setPlatformId     = createMutator<string>(platformId);
export const setPlatformName   = createMutator<string>(platformName);
export const setDeviceTypeId   = createMutator<string>(deviceTypeId);
export const setDeviceTypeName = createMutator<string>(deviceTypeName);

/**
 * Serializes a value into a stable string: object keys are sorted recursively
 * so key order never affects the result, and arrays keep their order.
 */
const stableSerialize = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map(
      (key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`
    ).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
};

/**
 * Returns a stable string of the user-editable form values: script name,
 * description, version, vehicle type, account id (normalized, so a global
 * group selector and a numeric id compare equal), device model, SIM APN,
 * inbound server address, port, selected modules (sorted, so toggling a module
 * off and on does not matter), module values and wake/sleep values.
 */
export const getFormSnapshot = (): string => stableSerialize({
  scriptName        : scriptName.value,
  scriptDescription : scriptDescription.value,
  scriptVersion     : scriptVersion.value,
  vehicleType       : vehicleType.value,
  accountId         : entityId(selectedAccountId.value) ?? null,
  deviceModel       : deviceModel.value,
  simApn            : simApn.value,
  inboundServer     : inboundServerAddress.value,
  port              : port.value,
  selectedModules   : [...selectedModules.value].sort(),
  moduleValues      : moduleValues.value,
  wakeSleepValues   : wakeSleepValues.value
});

/**
 * The form snapshot the wizard compares against to decide whether the user
 * has unsaved changes. It is the state at wizard open, or at the last
 * successful save.
 */
export const cleanSnapshot = signal<string>('');

/**
 * Records the current form values as the clean baseline.
 */
export const markClean = (): void => {
  cleanSnapshot.value = getFormSnapshot();
};

/**
 * `true` when the form values differ from the last clean baseline.
 */
export const isDirty = computed<boolean>(() => getFormSnapshot() !== cleanSnapshot.value);

/**
 * Runs a system write (defaults, hydration, account picker mount) that is not
 * a user edit. When the form was clean before the write it stays clean; when
 * it was already dirty the write leaves it dirty.
 */
export const applySystemChange = (fn: () => void): void => {
  const wasClean = !isDirty.value;
  try {
    fn();
  } finally {
    if (wasClean) {
      markClean();
    }
  }
};

export const canAdvance = computed(() => {
  if (activeStep.value === WizardStep.SCRIPT_DETAILS) {
    return (
      !!scriptName.value &&
      !scriptNameError.value &&
      !!scriptVersion.value &&
      !scriptVersionError.value &&
      !!vehicleType.value &&
      !!deviceModel.value &&
      !!inboundServerAddress.value &&
      !inboundServerAddressError.value &&
      !portError.value &&
      !isLoadingAccount.value
    );
  }
  return true;
});

/**
 * Checking if the selected vehicleType or deviceModel has changed from its previous value.
 */
const shouldLoadModuleProfile = computed(() => {
  const moduleProfileValue = moduleProfile.value;
  if (moduleProfileValue) {
    return !(
      vehicleType.value === moduleProfileValue.vehicleType &&
      deviceModel.value === moduleProfileValue.deviceModel
    );
  };
  return true;
});

const MAX_SCRIPT_NAME_LENGTH = 256;

export const onScriptNameChange = (value: string): void => {
  batch(() => {
    setScriptName?.(value);
    setScriptNameError?.(
      value.length > MAX_SCRIPT_NAME_LENGTH
        ? translate('peg-wizard:stepsCard.scriptDetails.nameTooLong')
        : ''
    );
  });
};

export const onScriptVersionChange = (value: string): void => {
  const isValid = !value || isValidScriptVersion(value);
  batch(() => {
    setScriptVersion?.(value);
    setScriptVersionError?.(
      isValid ? '' : translate('peg-wizard:stepsCard.scriptDetails.invalidVersion')
    );
  });
};

export const onInboundServerAddressChange = (value: string): void => {
  const isValid = !value || isValidIPAddress(value) || isValidHostname(value) || isValidDomain(value);
  batch(() => {
    setInboundServerAddress?.(value);
    setInboundServerAddressError?.(
      isValid ? '' : translate('peg-wizard:stepsCard.scriptDetails.invalidInboundServerAddress')
    );
  });
};

export const onPortChange = (value: string): void => {
  const isValid = !value || isValidPort(value);
  batch(() => {
    setPort?.(value);
    setPortError?.(
      isValid ? '' : translate('peg-wizard:stepsCard.scriptDetails.invalidPort')
    );
  });
};

/**
 * Before moving to Next step from Script Details:
 * Validating if Script Version and Script Name are taken. The version-taken
 * check only applies to new scripts, so it's skipped entirely in edit mode.
 */
export const onScriptDetailsNext = async(): Promise<void> => {
  const accountId               = entityId(selectedAccountId.value);
  const scriptNameWithExtension = getScriptNameWithExtension(scriptName.value);

  if (accountId) {
    try {
      setLoadingScriptVersion?.(true);
      const [isVersionTaken, isNameTaken] = await Promise.all([
        isEditMode.value ? Promise.resolve(false) : checkScriptVersionTaken(scriptVersion.value, accountId),
        isEditMode.value ? Promise.resolve(false) : checkScriptNameTaken(scriptNameWithExtension, accountId)
      ]);

      batch(() => {
        setScriptVersionError?.(
          isVersionTaken ? translate('peg-wizard:stepsCard.scriptDetails.versionTaken') : ''
        );
        setScriptNameError?.(
          isNameTaken ? translate('peg-wizard:stepsCard.scriptDetails.nameTaken') : ''
        );
      });

      if (!isVersionTaken && !isNameTaken) {
        goToNextStep();
      }
    } catch (err) {
      error('Failed to validate script details:', err);
    } finally {
      setLoadingScriptVersion?.(false);
    }
  };
};

/* Aggregates default values from all modules into a lookup object keyed by module name
 * Returns: { moduleName: { key: defaultValue, key: defaultValue }, ... }
 */
export const buildDefaults = (modulesToProcess) => {
  return modulesToProcess.reduce((acc, item) => {
    const pegWizardModule = item?.pegWizardModule || item;
    const name            = pegWizardModule.name ?? '';
    const groups          = composeModule(item);
    const flattenGroups   = flattenGroupDefaults(groups);
    const listGroups      = getListGroupDefaults(groups);
    return {...acc, [name]: {...flattenGroups, ...listGroups}};
  }, {});
};

const PEG2_FILE_TYPE_NAME = 'PEG2';

/**
 * Before publishing or republishing the assembled script on Save & Exit:
 * * Loading the platform device types to resolve the PEG2 file type's
 *   `deviceTypeId`/`deviceTypeName`/`platformId`/`platformName`, required
 *   by the files API request.
 */
export const loadPeg2PlatformInfo = async(): Promise<void> => {
  try {
    const deviceTypes      = await getPlatformDeviceTypes() as unknown as PlatformDeviceTypes;
    const peg2DeviceType   = deviceTypes
      ?.find(deviceType => deviceType.fileTypes.some(fileType => fileType.name === PEG2_FILE_TYPE_NAME));
    const peg2FileType     = peg2DeviceType?.fileTypes.find(fileType => fileType.name === PEG2_FILE_TYPE_NAME);

    setDeviceTypeId?.(peg2DeviceType?.id ?? '');
    setDeviceTypeName?.(peg2DeviceType?.name ?? '');
    setPlatformId?.(peg2FileType?.platformId ?? '');
    setPlatformName?.(peg2FileType?.platformName ?? '');
  } catch (err) {
    error('Failed to load PEG2 platform info:', err);
  }
};

/**
 * Returns `true` when every parameter in the group is a checkbox ('bool') type,
 * so it can lay out 4 items per row.
 */
export const isBooleanOnlyGroup = (group: ComposedParameterGroup): boolean => (
  group.parameters.length > 0 && (
    group.parameters.every((parameter) => parameter.definition?.type === 'bool')
  )
);
